import { useCallback, useEffect, useRef } from 'react';

const DRAG_ACTIVATION_DISTANCE = 4;
const SCROLLABLE_OVERFLOW_VALUES = new Set(['auto', 'overlay', 'scroll']);
const DRAG_SCROLL_IGNORED_TARGETS = [
  '[data-drag-scroll-ignore="true"]',
  '[contenteditable="true"]',
  'audio',
  'iframe',
  'input',
  'option',
  'select',
  'textarea',
  'video',
  '[role="slider"]',
].join(',');

const isIgnoredTarget = (target) => (
  typeof Element !== 'undefined' &&
  target instanceof Element &&
  Boolean(target.closest(DRAG_SCROLL_IGNORED_TARGETS))
);

const getScrollableAxes = (element) => {
  if (
    typeof HTMLElement === 'undefined' ||
    !(element instanceof HTMLElement) ||
    typeof window === 'undefined'
  ) {
    return { x: false, y: false };
  }

  const styles = window.getComputedStyle(element);
  return {
    x: SCROLLABLE_OVERFLOW_VALUES.has(styles.overflowX) &&
      element.scrollWidth > element.clientWidth + 1,
    y: SCROLLABLE_OVERFLOW_VALUES.has(styles.overflowY) &&
      element.scrollHeight > element.clientHeight + 1,
  };
};

export const findDragScrollTargets = (target, boundary) => {
  if (typeof HTMLElement === 'undefined' || !(boundary instanceof HTMLElement)) {
    return { x: null, y: null };
  }

  let element = typeof Element !== 'undefined' && target instanceof Element
    ? target
    : target?.parentElement;
  let horizontalTarget = null;
  let verticalTarget = null;

  while (element && boundary.contains(element)) {
    const axes = getScrollableAxes(element);
    if (!horizontalTarget && axes.x) {
      horizontalTarget = element;
    }
    if (!verticalTarget && axes.y) {
      verticalTarget = element;
    }
    if ((horizontalTarget && verticalTarget) || element === boundary) {
      break;
    }
    element = element.parentElement;
  }

  return { x: horizontalTarget, y: verticalTarget };
};

const setRootState = (root, state) => {
  if (!root) {
    return;
  }

  if (state) {
    root.dataset.dragScrollState = state;
  } else {
    delete root.dataset.dragScrollState;
  }
};

/**
 * Adds mouse drag-to-scroll to any scrollable descendants of the bound root.
 * Horizontal and vertical movement can target different nested containers.
 */
const useDragToScroll = () => {
  const dragStateRef = useRef(null);
  const bodyUserSelectRef = useRef(null);
  const clickResetTimerRef = useRef(null);
  const suppressClickRef = useRef(false);

  const clearClickResetTimer = useCallback(() => {
    if (clickResetTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(clickResetTimerRef.current);
      clickResetTimerRef.current = null;
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (bodyUserSelectRef.current !== null && typeof document !== 'undefined') {
      document.body.style.userSelect = bodyUserSelectRef.current;
      bodyUserSelectRef.current = null;
    }
  }, []);

  const finishDrag = useCallback((event) => {
    const dragState = dragStateRef.current;
    if (!dragState) {
      return;
    }

    if (event?.currentTarget?.hasPointerCapture?.(dragState.pointerId)) {
      event.currentTarget.releasePointerCapture(dragState.pointerId);
    }

    restoreSelection();
    setRootState(dragState.root, null);
    dragStateRef.current = null;

    if (dragState.active) {
      suppressClickRef.current = true;
      clearClickResetTimer();
      if (typeof window !== 'undefined') {
        clickResetTimerRef.current = window.setTimeout(() => {
          suppressClickRef.current = false;
          clickResetTimerRef.current = null;
        }, 0);
      }
    }
  }, [clearClickResetTimer, restoreSelection]);

  const updateReadyState = useCallback((event) => {
    if (dragStateRef.current || (event.pointerType && event.pointerType !== 'mouse')) {
      return;
    }

    const root = event.currentTarget;
    if (isIgnoredTarget(event.target)) {
      setRootState(root, null);
      return;
    }

    const targets = findDragScrollTargets(event.target, root);
    setRootState(root, targets.x || targets.y ? 'ready' : null);
  }, []);

  const handlePointerDown = useCallback((event) => {
    if (
      event.button !== 0 ||
      event.isPrimary === false ||
      (event.pointerType && event.pointerType !== 'mouse') ||
      isIgnoredTarget(event.target)
    ) {
      return;
    }

    const root = event.currentTarget;
    const targets = findDragScrollTargets(event.target, root);
    if (!targets.x && !targets.y) {
      return;
    }

    suppressClickRef.current = false;
    clearClickResetTimer();
    dragStateRef.current = {
      active: false,
      pointerId: event.pointerId,
      root,
      startX: event.clientX,
      startY: event.clientY,
      horizontalTarget: targets.x,
      verticalTarget: targets.y,
      initialScrollLeft: targets.x?.scrollLeft || 0,
      initialScrollTop: targets.y?.scrollTop || 0,
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [clearClickResetTimer]);

  const handlePointerMove = useCallback((event) => {
    const dragState = dragStateRef.current;
    if (!dragState) {
      updateReadyState(event);
      return;
    }
    if (event.pointerId !== dragState.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;

    if (!dragState.active && Math.hypot(deltaX, deltaY) < DRAG_ACTIVATION_DISTANCE) {
      return;
    }

    if (!dragState.active) {
      dragState.active = true;
      setRootState(dragState.root, 'active');
      if (typeof document !== 'undefined') {
        bodyUserSelectRef.current = document.body.style.userSelect;
        document.body.style.userSelect = 'none';
        window.getSelection?.()?.removeAllRanges();
      }
    }

    event.preventDefault();
    if (dragState.horizontalTarget) {
      dragState.horizontalTarget.scrollLeft = dragState.initialScrollLeft - deltaX;
    }
    if (dragState.verticalTarget) {
      dragState.verticalTarget.scrollTop = dragState.initialScrollTop - deltaY;
    }
  }, [updateReadyState]);

  const handleClickCapture = useCallback((event) => {
    if (!suppressClickRef.current) {
      return;
    }

    suppressClickRef.current = false;
    clearClickResetTimer();
    event.preventDefault();
    event.stopPropagation();
  }, [clearClickResetTimer]);

  const handlePointerLeave = useCallback((event) => {
    if (!dragStateRef.current) {
      setRootState(event.currentTarget, null);
    }
  }, []);

  useEffect(() => () => {
    restoreSelection();
    clearClickResetTimer();
    setRootState(dragStateRef.current?.root, null);
    dragStateRef.current = null;
  }, [clearClickResetTimer, restoreSelection]);

  return {
    onClickCapture: handleClickCapture,
    onPointerCancel: finishDrag,
    onPointerDown: handlePointerDown,
    onPointerLeave: handlePointerLeave,
    onPointerMove: handlePointerMove,
    onPointerOver: updateReadyState,
    onPointerUp: finishDrag,
  };
};

export default useDragToScroll;
